import type { Edge, Node } from '@xyflow/react';
import type { ELK, ElkExtendedEdge, ElkNode } from 'elkjs/lib/elk-api';
import type { GraphEdge } from '@nexus/skill-workflow';
import type { CanvasNode, CanvasGraph } from './canvas-graph';

export const STEP_NODE_SIZE = { width: 280, height: 88 };
export const END_NODE_SIZE = { width: 96, height: 44 };

export type WorkflowNodeData = { graphNode: CanvasNode };
export type EdgePoint = { x: number; y: number };
export type WorkflowEdgeData = { graphEdge: GraphEdge; points: EdgePoint[]; labelPosition?: EdgePoint };

// The layout engine is large. It loads the first time a flow is laid out, not with the app.
let elk: Promise<ELK> | null = null;
const loadElk = () => (elk ??= import('elkjs/lib/elk.bundled.js').then((module) => new module.default()));

const ROOT_OPTIONS_BASE = {
  'elk.algorithm': 'layered',
  'elk.direction': 'DOWN',
  'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
  'elk.edgeRouting': 'ORTHOGONAL',
  'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
  'elk.edgeLabels.placement': 'CENTER',
  'elk.json.edgeCoords': 'ROOT',
};

const LABEL_HEIGHT = 22;
const labelWidth = (text: string) => Math.ceil(text.length * 7 + 18);

const SPACING = {
  'elk.layered.spacing.nodeNodeBetweenLayers': '36',
  'elk.spacing.nodeNode': '80',
  'elk.layered.spacing.edgeNodeBetweenLayers': '24',
  'elk.spacing.edgeNode': '40',
  'elk.spacing.edgeEdge': '28',
  'elk.layered.spacing.edgeEdgeBetweenLayers': '28',
  'elk.spacing.edgeLabel': '10',
};

const ROOT_OPTIONS = { ...ROOT_OPTIONS_BASE, ...SPACING };
const GROUP_OPTIONS = { ...SPACING, 'elk.padding': '[top=64,left=40,bottom=40,right=40]' };

function sizeOf(node: CanvasNode): { width?: number; height?: number } {
  if (node.kind === 'end' || node.kind === 'start') return END_NODE_SIZE;
  if (node.kind === 'step') return STEP_NODE_SIZE;
  return {};
}

export async function layoutWorkflowGraph(
  graph: CanvasGraph
): Promise<{ nodes: Node<WorkflowNodeData>[]; edges: Edge<WorkflowEdgeData>[] }> {
  const children = new Map<string | null, CanvasNode[]>();
  for (const node of graph.nodes) {
    const list = children.get(node.parentId) ?? [];
    list.push(node);
    children.set(node.parentId, list);
  }
  const toElk = (node: CanvasNode): ElkNode => {
    const kids = children.get(node.id) ?? [];
    return {
      id: node.id,
      ...sizeOf(node),
      ...(kids.length > 0 || node.kind === 'section' || node.kind === 'repeat'
        ? { children: kids.map(toElk), layoutOptions: GROUP_OPTIONS }
        : {}),
    };
  };
  const edges: ElkExtendedEdge[] = graph.edges.map((e) => ({
    id: e.id,
    sources: [e.source],
    targets: [e.target],
    ...(e.label
      ? { labels: [{ id: `${e.id}:label`, text: e.label, width: labelWidth(e.label), height: LABEL_HEIGHT }] }
      : {}),
  }));
  const laidOut = await (
    await loadElk()
  ).layout({
    id: 'root',
    layoutOptions: ROOT_OPTIONS,
    children: (children.get(null) ?? []).map(toElk),
    edges,
  });

  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  const nodes: Node<WorkflowNodeData>[] = [];
  const visit = (elkNode: ElkNode, parentId: string | undefined) => {
    const graphNode = byId.get(elkNode.id) as CanvasNode;
    nodes.push({
      id: elkNode.id,
      type: graphNode.kind,
      position: { x: elkNode.x ?? 0, y: elkNode.y ?? 0 },
      width: elkNode.width,
      height: elkNode.height,
      data: { graphNode },
      draggable: false,
      connectable: false,
      ...(parentId ? { parentId } : {}),
    });
    for (const child of elkNode.children ?? []) visit(child, elkNode.id);
  };
  for (const node of laidOut.children ?? []) visit(node, undefined);

  const routed = new Map<string, ElkExtendedEdge>();
  const collect = (elkNode: ElkNode) => {
    for (const edge of elkNode.edges ?? []) routed.set(edge.id, edge);
    for (const child of elkNode.children ?? []) collect(child);
  };
  collect(laidOut);

  return {
    nodes,
    edges: graph.edges.map((graphEdge) => {
      const elkEdge = routed.get(graphEdge.id);
      const section = elkEdge?.sections?.[0];
      const points = section ? [section.startPoint, ...(section.bendPoints ?? []), section.endPoint] : [];
      const label = elkEdge?.labels?.[0];
      return {
        id: graphEdge.id,
        source: graphEdge.source,
        target: graphEdge.target,
        type: 'workflow',
        data: {
          graphEdge,
          points,
          ...(label && label.x !== undefined && label.y !== undefined
            ? { labelPosition: { x: label.x + (label.width ?? 0) / 2, y: label.y + (label.height ?? 0) / 2 } }
            : {}),
        },
      };
    }),
  };
}
